/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useState } from 'react';
import { Check, Link2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Copy a link to a circle.
 *
 * **The whole of the "reference a circle in an email" need.** `/c/:id` is a
 * doorbell rather than a key — see `server/accessRequests.js` — so handing the
 * address out costs nothing: whoever opens it is either let straight in or shown
 * a screen that offers to ask.
 *
 * It lives in two places, and this component is why they cannot drift: once per
 * row in the circle switcher, which is how anybody gets a link without being an
 * administrator, and once per circle in Manage circles, beside the other things
 * you do *to* a circle rather than *in* it.
 */
export function CopyCircleLink({
  circleId,
  name,
  className,
}: {
  circleId: string;
  name: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async (e: React.MouseEvent) => {
    // In the switcher this sits inside a row that switches circle on click.
    // Without this the click reaches the row behind it and switches, which is
    // the opposite of "I only wanted the address".
    e.stopPropagation();
    const url = `${window.location.origin}/c/${circleId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // The clipboard is refused in an insecure context and in some locked-down
      // profiles. A prompt is ugly, and it is also the only thing left that
      // lets somebody get the address at all.
      window.prompt('Copy this link', url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <button
      className={cn('circle-copy tap', className)}
      // **The name is in the label**, in both places. In the switcher there are
      // nine of these and "Copy link" nine times tells a screen reader nothing;
      // in the topbar it is the one thing that says which board is being copied.
      aria-label={`Copy a link to ${name}`}
      // A native tooltip rather than the app's `Tip`: in the switcher these sit
      // inside a popover, and a second floating layer on top of the first is a
      // hover target that moves when the menu scrolls.
      title={`Copy a link to ${name}`}
      onClick={copy}
    >
      {copied ? <Check className="size-3.5" aria-hidden /> : <Link2 className="size-3.5" aria-hidden />}
    </button>
  );
}
